(function () {
  'use strict';

  const CFG = (window && window.BOFU_OFFER) || {};
  if (!CFG.enabled) return;

  const LOG_PREFIX = '[BOFU offer]';
  const log = (...args) => {
    if (!CFG.debug) return;
    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, ...args);
  };

  const warn = (...args) => {
    // eslint-disable-next-line no-console
    console.warn(LOG_PREFIX, ...args);
  };

  const BOFU_PARAM = String(CFG.paramName || 'bofu_offer');
  const BOFU_VALUE = String(CFG.paramValue || '1');
  const COOKIE_NAME = String(CFG.cookieName || 'bofu_offer');
  const COOKIE_DAYS = Number.isFinite(Number(CFG.cookieDays))
    ? Number(CFG.cookieDays)
    : 7;
  const ADDED_COOKIE_NAME = `${COOKIE_NAME}_added`;

  const QUALIFYING_VARIANT_ID = Number(CFG.qualifyingVariantId);
  const FREE_VARIANT_ID = Number(CFG.freeVariantId);

  const ADD_QTY_QUALIFYING = Number(CFG.qualifyingQty) || 1;
  const ADD_QTY_FREE = Number(CFG.freeQty) || 1;

  function getParam(name) {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get(name);
    } catch (_) {
      return null;
    }
  }

  function setCookie(name, value, days) {
    const d = Number(days);
    const safeDays = Number.isFinite(d) ? d : 7;
    const expires = new Date(
      Date.now() + safeDays * 24 * 60 * 60 * 1000
    ).toUTCString();

    const safeValue = encodeURIComponent(String(value));
    document.cookie = `${name}=${safeValue}; expires=${expires}; path=/; SameSite=Lax`;
  }

  function getCookie(name) {
    try {
      const raw = document.cookie
        .split('; ')
        .find((row) => row.startsWith(name + '='));
      if (!raw) return null;
      const v = raw.split('=').slice(1).join('=');
      return decodeURIComponent(v);
    } catch (_) {
      return null;
    }
  }

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  function hasVariant(cart, variantId) {
    if (!variantId) return false;
    return !!cart.items?.some((i) => Number(i.variant_id) === Number(variantId));
  }

  async function addItems(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error('Failed to add items');
    return res.json();
  }

  function requestCartRefresh() {
    try {
      document.dispatchEvent(new CustomEvent('theme:update:cart'));
    } catch (_) {
      // no-op
    }
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function openCartDrawer() {
    const cartType = window?.theme?.settings?.cart_type;
    const cartUrl =
      window?.theme?.routes?.cart_url || window?.routes?.cart_url || '/cart';

    // If theme is configured to use the cart page, we can't open a drawer.
    if (cartType === 'page') {
      window.location.href = cartUrl;
      return;
    }

    // Best: open via Alpine modal store directly (what the header button does)
    try {
      if (window.Alpine && typeof window.Alpine.store === 'function') {
        const modals = window.Alpine.store('modals');
        if (modals && typeof modals.open === 'function') {
          modals.open('cart');
          return;
        }
      }
    } catch (_) {
      // continue
    }

    // Next best: theme event hook (handled in assets/theme-events.js)
    try {
      document.dispatchEvent(new Event('theme:open:cart-drawer'));
      return;
    } catch (_) {
      // continue to fallback
    }

    // Fallback: click something cart-ish (may navigate to /cart in some themes)
    const cartEl =
      document.getElementById('CartButton') ||
      document.querySelector('[data-cart-toggle]') ||
      document.querySelector('[data-open-cart]') ||
      document.querySelector('a[href="/cart"]') ||
      document.querySelector('a[href^="/cart"]');

    if (cartEl && typeof cartEl.click === 'function') cartEl.click();
  }

  async function openCartDrawerWhenReady() {
    // Alpine + stores can be late depending on bundling/execution order.
    // Retry briefly so we open reliably after async cart adds.
    const maxWaitMs = 2500;
    const intervalMs = 50;
    const startedAt = Date.now();

    while (Date.now() - startedAt < maxWaitMs) {
      try {
        if (window.Alpine && typeof window.Alpine.store === 'function') {
          const modals = window.Alpine.store('modals');
          if (modals && typeof modals.open === 'function') break;
        }
      } catch (_) {}
      await sleep(intervalMs);
    }

    openCartDrawer();
  }

  async function run() {
    // Sanity: require both IDs to be configured (via theme settings products)
    if (!Number.isFinite(QUALIFYING_VARIANT_ID) || QUALIFYING_VARIANT_ID <= 0) {
      log('Missing qualifying variant ID (check BOFU main product setting).');
      return;
    }
    if (!Number.isFinite(FREE_VARIANT_ID) || FREE_VARIANT_ID <= 0) {
      log('Missing free/discount variant ID (check BOFU free product setting).');
      return;
    }

    // 1) On BOFU landing, set cookie
    const triggeredThisVisit = getParam(BOFU_PARAM) === BOFU_VALUE;
    if (triggeredThisVisit) {
      setCookie(COOKIE_NAME, '1', COOKIE_DAYS);
      log('Offer cookie set due to URL param.');
    }

    // 2) If cookie not present, do nothing
    if (getCookie(COOKIE_NAME) !== '1') return;

    // 3) Ensure we only attempt the "initial add" once
    if (getCookie(ADDED_COOKIE_NAME) === '1') return;

    const cart = await getCart();
    const toAdd = [];

    if (!hasVariant(cart, QUALIFYING_VARIANT_ID)) {
      toAdd.push({ id: QUALIFYING_VARIANT_ID, quantity: ADD_QTY_QUALIFYING });
    }
    if (!hasVariant(cart, FREE_VARIANT_ID)) {
      toAdd.push({ id: FREE_VARIANT_ID, quantity: ADD_QTY_FREE });
    }

    if (toAdd.length) {
      log('Adding items:', toAdd);
      await addItems(toAdd);
      requestCartRefresh();
    } else {
      log('Items already present; no add needed.');
    }

    // Mark as "done" so we don't keep re-adding if items are later removed.
    setCookie(ADDED_COOKIE_NAME, '1', COOKIE_DAYS);

    if (CFG.openCartDrawer) await openCartDrawerWhenReady();
  }

  run().catch((err) => warn(err));
})();


